import { z } from 'zod';

/**
 * Dutch energy labels, ordered from best (A+++++) to worst (G).
 * The array order IS the sort order - do not reorder.
 */
export const ENERGIELABELS = [
  'A+++++',
  'A++++',
  'A+++',
  'A++',
  'A+',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
] as const;

export const energielabelSchema = z.enum(ENERGIELABELS);
export type Energielabel = z.infer<typeof energielabelSchema>;

/** Colour per label, roughly following the official Dutch label colours. */
export const ENERGIELABEL_KLEUREN: Record<Energielabel, string> = {
  'A+++++': '#00693e',
  'A++++': '#007a45',
  'A+++': '#008d4c',
  'A++': '#19a04f',
  'A+': '#4cb050',
  A: '#8cc63f',
  B: '#c3d600',
  C: '#ffe600',
  D: '#fdb913',
  E: '#f58220',
  F: '#ef4023',
  G: '#d0021b',
};

/** Colour used when a listing has no known energy label. */
export const ENERGIELABEL_ONBEKEND_KLEUR = '#9ca3af';

/** Rank of a label: 0 = best (A+++++), 11 = worst (G). */
export function energielabelVolgorde(label: Energielabel): number {
  return ENERGIELABELS.indexOf(label);
}

/** Type guard for unvalidated input (e.g. a scraped CSV column). */
export function isEnergielabel(waarde: unknown): waarde is Energielabel {
  return typeof waarde === 'string' && (ENERGIELABELS as readonly string[]).includes(waarde);
}

/**
 * Comparator for Array.prototype.sort: best label first.
 * `null` (unknown label) always sorts last.
 */
export function vergelijkEnergielabel(a: Energielabel | null, b: Energielabel | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return energielabelVolgorde(a) - energielabelVolgorde(b);
}

/** Colour for a label, with a neutral fallback for unknown labels. */
export function energielabelKleur(label: Energielabel | null | undefined): string {
  if (label == null) return ENERGIELABEL_ONBEKEND_KLEUR;
  return ENERGIELABEL_KLEUREN[label];
}

/**
 * Normalises loose source input ("a++", " A ", "label C") to a valid label.
 * Returns null when the value cannot be interpreted.
 */
export function parseEnergielabel(waarde: unknown): Energielabel | null {
  if (typeof waarde !== 'string') return null;
  const genormaliseerd = waarde
    .trim()
    .toUpperCase()
    .replace(/^LABEL\s*/, '')
    .replace(/\s+/g, '');
  return isEnergielabel(genormaliseerd) ? genormaliseerd : null;
}
